import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { MDB_TYPES } from "@meddbriefer/scenario-data/constants"

import { useScenarioType } from "../ScenarioTypeContext"

import InterventionsEditorLayout from "./InterventionEditorLayout"

import DetailsEditor from "../DetailsEditor"
import InlineEditor from "../InlineEditor"
import AddEntity from "../AddEntity"
import AssociateEntity from "../AssociateEntity"

import { genInterventionMenuItemRec } from "./types"
import { getIntvMenusBC, getIntvMenuCatBC } from "../urls"
import { swapDown, swapUp } from "../utils"

const InterventionsMenuCategory = () => {
    const params = useParams()
    const {intvMenuCategoryID} = params
    const {
        scenarioType,
        intvs,
        intvMenuCategories,
        intvMenuItems,
        uniqueIDs,
        uniqueLabels
    } = useScenarioType()
    const [cat, setCat] = useState(null)
    const [catUpdated, setCatUpdated] = useState(false)
    const [catItems, setCatItems] = useState(null)
    const [catItemsUpdated, setCatItemsUpdated] = useState(false)

    const updateCatField = (entityID, fld, val) => {
        setCat(prevState => ({...prevState, [fld]: val }))
        setCatUpdated(true)
    }

    const updateMenuItemField = (entityID, fldName, val) => {
        const itemRec = {...scenarioType.interventionMenuItems.find(e => e.id === entityID)}
        itemRec[fldName] = val
        intvMenuItems.update(itemRec)
    }
    const moveItemUp = (idx) => {
        updateCatField(cat.id, "items", swapUp(cat.items, idx))
        setCatItemsUpdated(true)
    }

    const moveItemDown = (idx) => {
        updateCatField(cat.id, "items", swapDown(cat.items, idx))
        setCatItemsUpdated(true)
    }

    const moveItemLeft = (idx) => {
        const item = {...catItems[idx]}
        item.depth -= 1
        intvMenuItems.update(item)
        setCatItemsUpdated(true)
    }

    const moveItemRight = (idx) => {
        const item = {...catItems[idx]}
        item.depth += 1
        intvMenuItems.update(item)
        setCatItemsUpdated(true)
    }

    const addMenuItem = (newMenuItem) => {
        intvMenuItems.add(newMenuItem)
        updateCatField(cat.id, "items", cat.items.concat(newMenuItem.id))
        setCatItemsUpdated(true)
    }

    const addInterventionMenuItem = (entityType, newID, interventionID) => {
        addMenuItem(
            genInterventionMenuItemRec(newID, getIntvLabel(interventionID), MDB_TYPES.INTERV, cat.id, interventionID)
        )
    }

    const addHeadingMenuItem = (entityType, newID, newLabel) => {
        addMenuItem(genInterventionMenuItemRec(newID, newLabel, "heading", cat.id))
    }

    const handleDeleteMenuItem = (item) => {
        intvMenuItems.remove(item.id)
        updateCatField(cat.id, "items", cat.items.filter(id => id !== item.id))
        setCatItemsUpdated(true)
    }

    const getIntvLabel = (intvID) => {
        const intv = scenarioType.interventions.find(e => e.id === intvID)
        if (!intv) {
            return `intervention "${intvID}" not found`
        }
        // return abbrLabel if it's defined and not empty string, else return label
        return (!intv.abbrLabel || "" === intv.abbrLabel) ? intv.label : intv.abbrLabel
    }

    const menuIDGenerator = (id) => id.replace("intv-", "menu-item-")

    useEffect(
        () => {
            if(undefined !== intvMenuCategoryID) {
                setCat(scenarioType.interventionMenuCategories.find(e => e.id === intvMenuCategoryID))
                setCatItemsUpdated(true)
            }
        }, [intvMenuCategoryID, scenarioType.interventionMenuCategories ]
    )

    useEffect(
        () => {
            if (catItemsUpdated) {
                const tmp = []
                const catMenuItems = scenarioType.interventionMenuItems.filter(e => e.intvCategoryID === cat.id)
                cat.items.forEach(intvMenuItemID => {
                    const match = catMenuItems.find(e => e.id === intvMenuItemID)
                    if (match) {
                        tmp.push(match)
                    } else {
                        console.log(`${cat.label} menu item ${intvMenuItemID} not found`)
                    }
                })
                setCatItems(tmp)
                setCatItemsUpdated(false)
            }
        }, [cat, catItemsUpdated, scenarioType.interventionMenuItems]
    )
    useEffect(
        () => {
            if (catUpdated) {
                intvMenuCategories.update(cat)
                setCatUpdated(false)
            }
        }, [cat, catUpdated, intvMenuCategories]
    )

    if (!catItems) {
        return (<div>Loading...</div>)
    }
    return (
        <InterventionsEditorLayout
            breadcrumbs = {
                [getIntvMenusBC(params), getIntvMenuCatBC(params, cat.label)]
            }
        >
            <DetailsEditor
                entity={cat}
                fields={[
                    { name: "id", label: "ID", widget: null },
                    { name: "label", label: "Label", widget: "text", changeHandler: updateCatField}
                ]}
            />
            <InlineEditor
                heading="Menu Items"
                records={catItems}
                fields={[
                    { name: "id", label: "ID", colWidthPct: 35, widget: "smallText" },
                    { name: "type", label: "Type", colWidthPct: 10, widget: "smallText" },
                    { name: "label", label: "Label", colWidthPct: 55, widget: "text", changeHandler: updateMenuItemField}
                ]}
                allowChangeOrder={true}
                onMoveUp={moveItemUp}
                onMoveDown={moveItemDown}
                allowChangeIndent={true}
                changeIndentAttr="depth"
                onMoveLeft={moveItemLeft}
                onMoveRight={moveItemRight}
                editLink={false}
                onDelete={handleDeleteMenuItem}
            >
                <AddEntity
                    entitiesDesc={[{
                        type: "heading",
                        name: "Heading"
                    }]}
                    addHandler={addHeadingMenuItem}
                    uniqueIDs={uniqueIDs}
                    uniqueLabels={uniqueLabels}
                />
                <AssociateEntity
                    entityDesc={{
                        type: "intervention",
                        name: "Intervention"
                    }}
                    choices={intvs.getSorted()}
                    addHandler={addInterventionMenuItem}
                    genIDs={true}
                    idGenerator={menuIDGenerator}
                    uniqueIDs={uniqueIDs}
                />
            </InlineEditor>
        </InterventionsEditorLayout>
    )
}

export default InterventionsMenuCategory